import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { useContext } from "react";
import Dashboard from './pages/Dashboard';
import Auction from "./pages/Auction";
import Guide from "./pages/Guide";
import DetailPage from "./pages/DetailPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import NotFound from "./pages/NotFound";
import AuthContext from "./store/auth-context";
import Management from "./pages/Management";
import Profile from "./pages/Profile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Shipping from "./pages/Shipping";
import DealerStock from "./pages/DealerStock";
import DealerStockDetail from "./pages/DealerStockDetail";
import Garage from "./pages/Garage";
import HolidayNotice from "./components/UI/HolidayNotice";
import { DealerFavoritesProvider } from "./store/dealer-favorites-context";

function App() {
  const authCtx = useContext(AuthContext);
  const isLoggedIn = authCtx.isLoggedIn;
  const isAdmin = authCtx.userData?.userRole === "administrator";

  return (
    <BrowserRouter>
      <DealerFavoritesProvider>
      <Routes>
        {/* Public policy pages - accessible to everyone */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/shipping" element={<Shipping />} />

        {/* Password reset must resolve whatever the local session looks like. A stale
            token in storage still reads as "logged in" here even though the server has
            rejected it, and gating this route on !isLoggedIn sent those users to the
            404 page instead of the reset form. */}
        <Route path="/forgot-password/:token?" element={<ForgotPassword/>} />

        {
          !isLoggedIn && (
            <>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            {/* /signin and /signup are the paths the marketing site, the
                onboarding emails and the Google Ads conversion tracking all
                link to. They had no route here, so every one of those links
                landed on the 404 page. */}
            <Route path="/signin" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Register />} />
            </>
          )
        }
        {
          isLoggedIn && (
            <>
            <Route path="/" element={<Dashboard />} />
            <Route path="/auction" element={<Auction />} />
            <Route path="/dealer-stock" element={<DealerStock />} />
            {/* Slug URLs carry the vehicle name the way the marketing site's
                canonical /dealer-stock/:slug/:id does, so a link shared out of
                the portal reads as a car rather than as a hex id. The bare
                /dealer-stock/:id form stays registered because every link
                minted before this change uses it — both resolve on `id`. */}
            <Route path="/dealer-stock/:slug/:id" element={<DealerStockDetail />} />
            <Route path="/dealer-stock/:id" element={<DealerStockDetail />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/detail/:id" element={<DetailPage />} />
            {/* Saved cars, inquiries, inspections, purchases and invoices, from
                the auctions and from dealer stock. The dealer-stock records are
                shared with the marketing site through the same account. */}
            <Route path="/garage" element={<Garage />} />
            {/* The auction inspections, purchases and favourites pages moved
                into My Garage. Bookmarks, older emails and the purchase
                notifications (link "/purchase") still use these paths. */}
            <Route path="/inspection" element={<Navigate to="/garage?source=auction&tab=inspections" replace />} />
            <Route path="/purchase" element={<Navigate to="/garage?source=auction&tab=purchases" replace />} />
            <Route path="/favorites" element={<Navigate to="/garage?source=auction&tab=favorites" replace />} />
            <Route path="/login" element={<Login />} />
            {/* Login handles the ?redirect= hand-back to the marketing site
                for an already signed-in visitor too. */}
            <Route path="/signin" element={<Login />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="/profile" element={<Profile/> } />
            </>
          )
        }
        {
          isLoggedIn && isAdmin && (
            <>
            <Route path="/management" element={<Management />} />
            </>
          )
        }
          <Route path="*" element={<NotFound/>} />
      </Routes>
      </DealerFavoritesProvider>
      <HolidayNotice />
    </BrowserRouter>
  );
}

export default App;
